import React, { useRef, useMemo } from "react";

import { Text, Group, Rect } from "react-konva";
import Konva from "konva";

import {
  SubtitleStyles,
  BackgroundStyle,
  LetterCase,
  REFERENCE_PREVIEW_HEIGHT,
} from "../../types";

interface SubtitleRendererProps {
  text: string;
  styles: SubtitleStyles;
  videoDimensions: { width: number; height: number };
  handlePositionX?: number;
  handlePositionY?: number;
  handleWidth?: number;
}

export default function SubtitleRenderer({
  text,
  styles,
  videoDimensions,
  handlePositionX,
  handlePositionY,
  handleWidth,
}: SubtitleRendererProps) {
  const textRef = useRef<Konva.Text>(null);
  const rectRef = useRef<Konva.Rect>(null);
  const groupRef = useRef<Konva.Group>(null);

  const displayText = useMemo(() => {
    switch (styles.letter_case) {
      case LetterCase.UPPERCASE:
        return text.toUpperCase();
      case LetterCase.LOWERCASE:
        return text.toLowerCase();
      case LetterCase.CAPITALIZE:
        return text.replace(/\b\w/g, (char) => char.toUpperCase());
      default:
        return text;
    }
  }, [text, styles.letter_case]);

  const centerX =
    ((handlePositionX ?? styles.text_handle_position_x_percentage ?? 50) /
      100) *
    videoDimensions.width;
  const y =
    ((handlePositionY ?? styles.text_handle_position_y_percentage ?? 80) /
      100) *
    videoDimensions.height;
  const width = Math.max(
    0,
    ((handleWidth ?? styles.text_handle_width_percentage ?? 80) / 100) *
      videoDimensions.width,
  );

  const groupX = centerX;

  const fontScale = videoDimensions.height / REFERENCE_PREVIEW_HEIGHT;
  const scaledFontSize = Math.round((styles?.size || 24) * fontScale);

  const backgroundHeight = useMemo(() => {
    const lineHeight = styles?.line_height || 1.2;
    const scaledPadding = Math.round(40 * fontScale);

    const textLength = displayText.length;
    if (textLength === 0) return 0;

    const charsPerLine = Math.floor(
      (width - scaledPadding) / (scaledFontSize * 0.6),
    );
    const estimatedLines = Math.max(1, Math.ceil(textLength / charsPerLine));
    return Math.max(
      Math.round(60 * fontScale),
      Math.floor(scaledFontSize * lineHeight * estimatedLines) + scaledPadding,
    );
  }, [displayText, width, scaledFontSize, fontScale, styles?.line_height]);

  if (!displayText) return null;

  return (
    <Group ref={groupRef} x={groupX} y={y} opacity={1}>
      <Rect
        ref={rectRef}
        x={0}
        y={0}
        width={width}
        height={backgroundHeight}
        fill={
          styles?.background_style !== BackgroundStyle.NONE
            ? styles?.background_color || "black"
            : "transparent"
        }
        opacity={0.8}
        cornerRadius={
          styles?.background_style === BackgroundStyle.ROUNDED ? 10 : 0
        }
      />
      <Text
        ref={textRef}
        x={0}
        y={0}
        text={displayText}
        width={width}
        fontSize={scaledFontSize}
        fill={styles?.color || "white"}
        fontFamily={styles?.font || "Bebas Neue"}
        fontStyle={(() => {
          if (styles?.italic && styles?.bold) return "italic bold";
          if (styles?.italic) return "italic";
          if (styles?.bold) return "bold";
          return "normal";
        })()}
        textDecoration={styles?.underline ? "underline" : ""}
        lineHeight={styles?.line_height}
        letterSpacing={styles?.letter_spacing}
        align={styles?.text_align || "center"}
        verticalAlign="middle"
        stroke={styles?.outline_color}
        strokeWidth={styles?.outline_thickness}
        wrap="word"
        padding={Math.round(20 * fontScale)}
        listening={false}
      />
    </Group>
  );
}
